/* eslint-disable indent */
import { Box, Chip, FormControlLabel, IconButton, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import DialogComponent from 'components/Dialog';
import Icon from 'components/Icon/Icon';
import { SwitchCus } from 'components/SwitchCus/SwitchCus';
import { IItemCart } from 'features/cart/types/CartTypes';
import { useStore } from 'features/home/store/homeSlice';
import { IRestaurant } from 'features/home/types/homeTypes';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { showIsNotTakeaway, showSwitchTakeaway } from 'submodules/OrderCore';
import { theme } from 'theme';
import useAnalyticsEventTracker from 'utils/analytics/useAnalyticsEventTracker';
import { EEventAction, EEventCategory } from 'utils/constants';
import { isSameString } from 'utils/restaurantFeatures';
import { shallow } from 'zustand/shallow';

export const SecondaryTextRed = styled(Typography)`
  color: red;
  font-size: 16px;
`;

const deleteIconSize = 20;
const paddingdeleteButton = 5;
const deleteButtonWidth = deleteIconSize + paddingdeleteButton * 2 + 10;
const useStyles = makeStyles({
  cartItemsBox: {
    borderBottom: `1px solid  ${theme.palette.text.secondary}`,
    marginBottom: 10,
  },
  cartItem: {
    marginTop: 20,
    alignItems: 'center',
  },
  textGray: {
    fontSize: 14,
    lineHeight: '2.4rem',
    color: theme.palette.text.secondary,
  },
  textBold: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '2.4rem',
    color: theme.palette.grey[900],
  },
  deleteButton: {
    padding: paddingdeleteButton,
    marginLeft: 10,
    '& svg': {
      width: 20,
      height: 20,
      '& circle': {
        stroke: theme.palette.grey[800],
      },
      '& path': {
        stroke: theme.palette.grey[800],
      },
    },
  },
  deleteIcon: {
    height: deleteIconSize,
    width: deleteIconSize,
  },
  quantityItem: {
    width: 33,
    flexShrink: 0,
  },
  price: {
    textAlign: 'center',
    padding: '0 5px',
  },
  nameFlex: {
    flex: 3,
  },
  nameBox: {
    flex: 3,
    paddingRight: 5,
  },
  name: {
    wordBreak: 'break-word',
  },
  altName: {
    fontWeight: 200,
    wordBreak: 'break-word',
    color: theme.palette.grey[900],
  },
  deleteButtonWidth: {
    width: deleteButtonWidth,
  },
  optionName: {
    fontSize: 14,
    lineHeight: '2.4rem',
    color: theme.palette.primary.main,
  },
  breakLine: {
    width: 73,
    borderBottom: `1px solid ${theme.palette.text.secondary}`,
    marginBlock: 9,
  },
  labelSwitch: {
    color: theme.palette.grey[800],
  },
  tag: {
    fontSize: 10,
    lineHeight: '10px',
    height: 24,
    margin: '0px 3px 2px 10px',
    backgroundColor: '#e0e0e0',
    color: '#000000',
    '& .MuiChip-label-131': {
      fontWeight: 'bold !important',
    },
    [theme.breakpoints.down('xs')]: {
      height: 20,
      '& .MuiChip-label': {
        padding: '0 6px',
      },
    },
  },
  quantity: {
    width: 30,
    textAlign: 'center',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '2.4rem',
    display: 'inline',
    color: theme.palette.grey[800],
  },
  countActionBox: {
    alignItems: 'center',
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '0 12px',
    height: 40,
    borderRadius: 8,

    '&:hover': {
      backgroundColor: theme.palette.grey[900],
    },
  },
  countButton: {
    padding: 5,
    '& svg': {
      width: 20,
      height: 20,
      '& circle': {
        stroke: theme.palette.grey[800],
      },
      '& path': {
        stroke: theme.palette.grey[800],
      },
    },
  },
  countButtonAdd: {
    padding: 5,
    '& svg': {
      width: 20,
      height: 20,
      '& circle': {
        stroke: theme.palette.grey[800],
      },
    },
  },
});

interface CartItemProps {
  hasRemove?: boolean;
  cartItem: IItemCart;
  index: number;
  onRemoveItem?: (index: number) => void;
  onSwitchTakeawayItem?: (index: number) => void;
  tableId?: string;
  restaurant: IRestaurant;
  isSoldOut?: boolean;
  onIncreaseItem?: (index: number) => void;
}

function CartItem({
  cartItem,
  onSwitchTakeawayItem,
  tableId,
  index,
  hasRemove = true,
  onRemoveItem,
  restaurant,
  isSoldOut = false,
  onIncreaseItem,
}: CartItemProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isDineInTable } = useStore(
    (state) => ({
      isDineInTable: state.homeState.isDineInTable,
    }),
    shallow,
  );
  const [openDialog, setOpenDialog] = useState(false);
  const eventTracker = useAnalyticsEventTracker();
  const renderGroupName = (item: any) => {
    const groupId = item?.groupId || '';
    const currentGroup = cartItem.dish.groups.find((gr: any) => gr.id === groupId);
    return currentGroup?.name || '';
  };

  const handleRemoveItem = () => {
    if (cartItem.quantity === 1) {
      setOpenDialog(true);
      return;
    }
    onRemoveItem && onRemoveItem(index);
    eventTracker(
      EEventCategory.DECREASE_QUANTITY_EVENT,
      EEventAction.DECREASE,
      cartItem?.dish?.name || '',
    );
  };

  const handleIncreaseQuantity = () => {
    onIncreaseItem && onIncreaseItem(index);
    eventTracker(
      EEventCategory.INCREASE_QUANTITY_EVENT,
      EEventAction.INCREASE,
      cartItem?.dish?.name || '',
    );
  };
  const handleCancelConfirmDeleteItem = () => {
    setOpenDialog(false);
  };

  const handleContinueConfirmDeleteItem = () => {
    setOpenDialog(false);
    onRemoveItem && onRemoveItem(index);
    eventTracker(
      EEventCategory.DECREASE_QUANTITY_EVENT,
      EEventAction.DECREASE,
      cartItem?.dish?.name || '',
    );
  };

  return (
    <Box className={classes.cartItemsBox}>
      {index === 0 && (
        <Box display="flex">
          <Typography className={clsx(classes.textGray, classes.quantityItem)}>
            {t('Qty')}
          </Typography>
          <Typography className={classes.quantityItem}></Typography>
          <Typography className={clsx(classes.textGray, classes.nameFlex)}>{t('Item')}</Typography>
          <Typography
            className={clsx(classes.textGray, classes.price)}
            style={{ minWidth: '130px' }}
          >
            {t('Price')}
          </Typography>
        </Box>
      )}
      <Box display="flex" className={classes.cartItem}>
        <Typography className={clsx(classes.textBold, classes.quantityItem)}>
          {cartItem.quantity}
        </Typography>
        <Typography className={clsx(classes.textBold, classes.quantityItem)}>X</Typography>
        <Box display="flex" flexDirection="column" className={classes.nameBox}>
          <Typography className={clsx(classes.textBold, classes.name)}>
            {cartItem.dish.name}
            {isSoldOut && <Chip label={'Sold Out'} className={classes.tag} />}
          </Typography>
          <Typography className={classes.altName}>
            {!isSameString(cartItem.dish.altName, cartItem.dish.name) && cartItem.dish.altName}
          </Typography>
          {!hasRemove && (
            <Typography className={clsx(classes.textGray, classes.name)}>
              {cartItem.dish.description}
            </Typography>
          )}
        </Box>
        {hasRemove && (
          <Box display="flex" className={classes.countActionBox}>
            <IconButton onClick={handleRemoveItem} className={classes.countButton}>
              <Icon name="minus" />
            </IconButton>
            <Typography className={clsx(classes.textBold, classes.price)}>
              {restaurant && restaurant.currencySymbol ? restaurant.currencySymbol : '$'}{' '}
              {Number((cartItem.subTotal * cartItem.quantity).toFixed(2))}
            </Typography>
            <IconButton className={classes.countButtonAdd} onClick={handleIncreaseQuantity}>
              <Icon name="addFilled" />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box display="flex">
        <Typography className={clsx(classes.optionName, classes.quantityItem)}></Typography>
        <Typography className={classes.quantityItem}></Typography>
        <Box className={clsx(classes.optionName, classes.nameFlex)}>
          <Box className={classes.breakLine} />
          {cartItem.size?.name && (
            <span>
              -{t('Size')}: {cartItem.size.name}
            </span>
          )}
        </Box>
        <Typography className={clsx(classes.optionName, classes.price)}></Typography>
        {hasRemove && <div className={classes.deleteButtonWidth}></div>}
      </Box>

      <Box>
        {cartItem.options.map((item: any, index: number) => (
          <Box key={index.toString()} display="flex">
            <Typography className={clsx(classes.optionName, classes.quantityItem)}></Typography>
            <Typography className={classes.quantityItem}></Typography>
            <Box className={clsx(classes.optionName, classes.nameFlex)}>
              <span>
                -{renderGroupName(item)}: {item.name}
              </span>
              <span>{item.quantity > 1 && ` x ${item.quantity}`}</span>
            </Box>
            <Typography className={clsx(classes.optionName, classes.price)}></Typography>
            {hasRemove && <div className={classes.deleteButtonWidth}></div>}
          </Box>
        ))}
      </Box>
      {tableId &&
        hasRemove &&
        showSwitchTakeaway(tableId, restaurant, !!cartItem?.dish?.isTakeawayDisabled) && (
          <FormControlLabel
            classes={{
              label: classes.labelSwitch,
            }}
            control={
              <SwitchCus
                checked={cartItem.isTakeaway}
                onChange={() => onSwitchTakeawayItem && onSwitchTakeawayItem(index)}
                name="isSwitchTakeaway"
              />
            }
            label="For takeaway"
          />
        )}
      {hasRemove &&
        tableId &&
        showIsNotTakeaway(tableId, restaurant, !!cartItem?.dish?.isTakeawayDisabled) && (
          <Typography className={classes.labelSwitch}>
            {t('*Not available for takeaway')}
          </Typography>
        )}
      {!hasRemove && cartItem.isTakeaway && <SecondaryTextRed>* Takeaway</SecondaryTextRed>}
      <DialogComponent
        message={'Will this item be removed from the cart? Please confirm the deletion.'}
        openDialog={openDialog}
        handleCancel={handleCancelConfirmDeleteItem}
        handleContinue={handleContinueConfirmDeleteItem}
      />
    </Box>
  );
}

export default CartItem;
